import { isAndroid, isIOS } from 'react-device-detect';
import { SITE_ORIGIN } from '../../constants/categorySeoSlugs';
import { SOUNDPILL_OPEN_PAGE_URL } from '../openSoundPillApp';
import { SESSION_RESULT_OG_IMAGE } from './sessionResultSeo';

export type SessionShareResult = 'kakao' | 'native' | 'copied' | 'cancelled' | 'failed';

type ShareSessionResultOptions = {
  title: string;
  description: string;
  url?: string;
  imageUrl?: string;
};

type KakaoShareSdk = {
  isInitialized: () => boolean;
  init: (key: string) => void;
  Share: {
    sendDefault: (payload: Record<string, unknown>) => void;
    sendScrap: (payload: { requestUrl: string }) => void;
  };
};

declare global {
  interface Window {
    Kakao?: KakaoShareSdk;
  }
}

const KAKAO_SDK_URL = 'https://t1.kakaocdn.net/kakao_js_sdk/2.7.4/kakao.min.js';
const DEFAULT_SHARE_IMAGE = SESSION_RESULT_OG_IMAGE;
const PRODUCTION_SHARE_ORIGIN = SITE_ORIGIN;
const KAKAO_SHARE_PATH = '/qr-session-result';

/** 카카오 공유 URL에 포함할 최소 쿼리 (패킷 10KB·도메인 검증 대응) */
const KAKAO_SHARE_STRESS_KEYS = ['v', 'sid', 'bpm', 'hrv', 'relax', 'stress', 'min', 't'] as const;
const KAKAO_SHARE_COGNITIVE_KEYS = [
  'v',
  'type',
  'sid',
  'vs',
  'vsT',
  'mem',
  'memT',
  'rsn',
  'rsnT',
  't',
] as const;
const KAKAO_SHARE_MINDREST_KEYS = [
  'v',
  'type',
  'sid',
  'before',
  'after',
  't',
  'hl',
  'sl',
  'set',
] as const;

const KAKAO_FEED_TITLE_MAX = 80;
const KAKAO_FEED_DESC_MAX = 120;

const KAKAO_SHARE_DEFAULT_TITLE = '나의 스트레스 완화 세션';
const KAKAO_SHARE_DEFAULT_DESC = '사운드필 키오스크 스트레스 세션 측정 결과입니다.';

/** 카카오 JS SDK 공유가 허용되는 웹 도메인 (카카오 개발자 콘솔 등록 필요) */
const KAKAO_SDK_ALLOWED_HOSTS = new Set([
  'soundpill.app',
  'www.soundpill.app',
  'dev.soundpill.app',
]);

function isLocalDevHost(hostname: string): boolean {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true;
  return false;
}

function canUseKakaoSdk(): boolean {
  const { hostname } = window.location;
  if (isLocalDevHost(hostname)) return false;
  return KAKAO_SDK_ALLOWED_HOSTS.has(hostname);
}

/** 카카오 [제품 링크 관리] 등록 도메인(https://soundpill.app) 기준 URL */
export function buildCompactSessionResultShareUrl(search = window.location.search): string {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(raw);
  const compact = new URLSearchParams();
  const sessionType = params.get('type')?.trim().toLowerCase();
  const keys =
    sessionType === 'cognitive'
      ? KAKAO_SHARE_COGNITIVE_KEYS
      : sessionType === 'mindrest'
        ? KAKAO_SHARE_MINDREST_KEYS
        : KAKAO_SHARE_STRESS_KEYS;

  for (const key of keys) {
    const value = params.get(key);
    if (value) compact.set(key, value);
  }

  const qs = compact.toString();
  return `${PRODUCTION_SHARE_ORIGIN}${KAKAO_SHARE_PATH}${qs ? `?${qs}` : ''}`;
}

function normalizeKakaoProductLinkUrl(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.protocol = 'https:';
    parsed.hostname = 'soundpill.app';
    return parsed.toString();
  } catch {
    return buildCompactSessionResultShareUrl();
  }
}

function truncateKakaoText(value: string, max: number): string {
  const trimmed = value.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1)}…`;
}

/** 로컬/사설 IP에서는 카카오 공유용 프로덕션 URL 사용 */
export function getSessionResultShareUrl(): string {
  return buildCompactSessionResultShareUrl(window.location.search);
}

function resolveKakaoJsKey(): string | undefined {
  const hostname = window.location.hostname;
  const isProdHost = hostname === 'soundpill.app' || hostname === 'www.soundpill.app';

  if (isProdHost) {
    return (
      process.env.REACT_APP_KAKAO_PROD_CLIENT_ID?.trim() ||
      process.env.REACT_APP_KAKAO_CLIENT_ID?.trim()
    );
  }

  return process.env.REACT_APP_KAKAO_CLIENT_ID?.trim();
}

let kakaoSdkPromise: Promise<void> | null = null;

function loadKakaoSdk(): Promise<void> {
  if (window.Kakao) return Promise.resolve();

  if (!kakaoSdkPromise) {
    kakaoSdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = KAKAO_SDK_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Kakao SDK load failed'));
      document.head.appendChild(script);
    });
  }

  return kakaoSdkPromise;
}

function initKakaoSdk(): boolean {
  const key = resolveKakaoJsKey();
  if (!key || !window.Kakao) return false;

  if (!window.Kakao.isInitialized()) {
    window.Kakao.init(key);
  }

  return window.Kakao.isInitialized();
}

async function shareViaKakao({ title, description, url, imageUrl }: ShareSessionResultOptions & { url: string }) {
  await loadKakaoSdk();
  if (!initKakaoSdk()) {
    throw new Error('Kakao SDK init failed');
  }

  const linkUrl = normalizeKakaoProductLinkUrl(url);
  const appOpenUrl = SOUNDPILL_OPEN_PAGE_URL;

  window.Kakao!.Share.sendDefault({
    objectType: 'feed',
    content: {
      title: truncateKakaoText(title || KAKAO_SHARE_DEFAULT_TITLE, KAKAO_FEED_TITLE_MAX),
      description: truncateKakaoText(description || KAKAO_SHARE_DEFAULT_DESC, KAKAO_FEED_DESC_MAX),
      imageUrl: imageUrl ?? DEFAULT_SHARE_IMAGE,
      link: {
        mobileWebUrl: linkUrl,
        webUrl: linkUrl,
      },
    },
    // feed 타입: buttons 최대 2개 (말풍선 본문 탭 = '자세히 보기' → content.link)
    buttons: [
      {
        title: '결과 확인하기',
        link: {
          mobileWebUrl: linkUrl,
          webUrl: linkUrl,
        },
      },
      {
        title: '사운드필 열기',
        link: {
          mobileWebUrl: appOpenUrl,
          webUrl: appOpenUrl,
        },
      },
    ],
  });
}

function copyTextFallback(text: string): boolean {
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.width = '2em';
    textarea.style.height = '2em';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();

    if (navigator.userAgent.match(/ipad|iphone/i)) {
      const range = document.createRange();
      range.selectNodeContents(textarea);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      textarea.setSelectionRange(0, text.length);
    } else {
      textarea.select();
    }

    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
}

async function copyShareText(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through */
    }
  }

  return copyTextFallback(text);
}

async function shareViaNative(title: string, text: string, url: string): Promise<'native' | 'cancelled'> {
  // iOS Safari는 text+url 동시 전달 시 실패하는 경우가 많음
  if (isIOS) {
    await navigator.share({ text });
    return 'native';
  }

  await navigator.share({ title, text, url });
  return 'native';
}

/** Android 로컬/HTTP 등 SDK 불가 환경 — 카카오톡 앱으로 텍스트 공유 */
function tryOpenKakaoTalkAndroid(shareText: string): boolean {
  if (!isAndroid) return false;

  try {
    const encoded = encodeURIComponent(shareText);
    window.location.href = `intent://send?text=${encoded}#Intent;scheme=kakaotalk;package=com.kakao.talk;end`;
    return true;
  } catch {
    return false;
  }
}

/** QR 세션 결과 — 카카오톡 공유 (SDK → 시스템 공유 → 링크 복사) */
export async function shareSessionResultPage({
  title,
  description,
  url = getSessionResultShareUrl(),
  imageUrl,
}: ShareSessionResultOptions): Promise<SessionShareResult> {
  const shareText = [description, url].filter(Boolean).join('\n\n');

  if (canUseKakaoSdk()) {
    try {
      await shareViaKakao({ title, description, url, imageUrl });
      return 'kakao';
    } catch {
      /* SDK 로드/init 실패 — 폴백 */
    }
  }

  if (tryOpenKakaoTalkAndroid(shareText)) {
    return 'kakao';
  }

  if (typeof navigator.share === 'function') {
    try {
      return await shareViaNative(title, shareText, url);
    } catch (error) {
      if ((error as Error).name === 'AbortError') return 'cancelled';
    }
  }

  const copied = await copyShareText(shareText);
  return copied ? 'copied' : 'failed';
}

export function sessionShareFeedbackMessage(result: SessionShareResult): string | null {
  switch (result) {
    case 'native':
      return null;
    case 'copied':
      return '링크가 복사되었어요. 카카오톡에 붙여넣어 공유해 주세요.';
    case 'failed':
      return '공유할 수 없어요. 주소창 URL을 직접 복사해 주세요.';
    case 'cancelled':
      return null;
    default:
      return null;
  }
}
